import { contentToText, type AgentSubscriber, type Context, type HttpAgent } from "@ag-ui/client";
import { createSparkAgent, runWatchedTurn } from "./agui-transport";
import { parseAgentNoticeCode, type AgentNoticeCode } from "./agent-notice";
import type { SearchSource } from "./research-sources";
import type { StudioNotebookContext } from "./studio-proposal";

// The Assistant chat turn over AG-UI (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption), on the transport in
// agui-transport.ts, pointed at the BFF /ai/agui passthrough (which injects the Bearer,
// exactly like /ai/stream). Sessions stay server-authoritative: the agent-service reads only the
// LAST user message of `messages[]`, so we set exactly that one message per run instead of
// accumulating client history. The @adr SPARK-0014-design-primitives-single-source@1 person-load endpoints still use the legacy SSE parser
// in agent-stream.ts.

export function createAssistantAgent(): HttpAgent {
  return createSparkAgent("ai/agui");
}

// The drawer's view of a turn — same shape the legacy SSE callbacks fed, so the store wiring
// is unchanged. `onToolCall` fires at TOOL_CALL_END with the fully-parsed args.
export interface AguiTurnHandlers {
  onNotice: (code: AgentNoticeCode) => void;
  onThreadId: (id: string) => void;
  // Fires at RUN_STARTED with the id of THIS run. It is what a Stop names: the turn the user is
  // looking at, rather than "whatever is running now" — which is the wrong turn if they press Stop
  // as it ends and have already asked the next question. The client mints no run id of its own;
  // @ag-ui/client makes one per run and the server echoes it back here.
  onRunId: (runId: string) => void;
  onDelta: (delta: string) => void;
  onToolCall: (name: string, args: Record<string, unknown>, toolCallId: string) => void;
  // Fires at TOOL_CALL_RESULT with the call's outcome. A completed tool call can still be a
  // failure (`isError` true) — correlate by `toolCallId` to the matching onToolCall to gate
  // success-only side effects.
  onToolResult: (toolCallId: string, result: ToolResultMeta) => void;
  onSuggestions: (items: string[]) => void;
  // Fires on a `progress` CUSTOM event: the named stage the turn is in
  // (@external-adr AGENT-SERVICE-0030-assistant-progress-channel). The phase is a server-side
  // vocabulary, so nothing type-checks it here - unknown phases render a generic label.
  onProgress: (progress: AgentProgress) => void;
  // Fires at RUN_FINISHED with what the server states about the whole run (see RunResult). Not
  // called on RUN_ERROR or a stall, so a failed turn never gets flagged.
  onRunResult: (result: RunResult) => void;
  onError: () => void;
}

// One predicate of the query behind a stage. `op` is the schema operator name (`contains`, `eq`,
// `in`, `any`, …), never a rendered symbol: the wording is ours, so a German line does not end up
// carrying `AND` or `is set` (@external-adr AGENT-SERVICE-0030-assistant-progress-channel).
export interface FilterCondition {
  field: string;
  op: string;
  value?: string;
  values: string[];
}

// A whole `where` clause, flattened. Absent when agent-service could not flatten it (a `not`, or
// logic nested inside logic) — then `params.filter` carries a rendered fragment instead.
export interface FilterDescription {
  logic: string;
  conditions: FilterCondition[];
}

// A named stage of a turn. `phase` is deliberately a plain string: the vocabulary lives in the
// agent-service enum, and a phase this build does not know must degrade rather than disappear.
export interface AgentProgress {
  phase: string;
  params: Record<string, string>;
  filter?: FilterDescription;
  // How deeply nested the work is: absent or 0 in the main loop, 1 inside a sub-exploration. The
  // drawer indents by it, so a condition's queries read as belonging to the condition above them.
  // Optional because agent-service omits it at 0, which is most lines.
  depth?: number;
}

export interface ToolResultMeta {
  isError: boolean;
  resultId: string | null;
}

// What the server states about a finished run, off RUN_FINISHED.result. `externalSources` is true
// when the answer carries content researched on the public web
// (@external-adr AGENT-SERVICE-0034-web-search-as-a-separate-tool): agent-service derives it from the
// turn's WebSearch step, so the client never inspects tool payloads for it.
export interface RunResult {
  externalSources: boolean;
}

// Server-shaped, so validated rather than cast. A result without the key (an older agent-service,
// an ephemeral debug turn) reads as internal-only, which is the safe default for a notice.
function parseRunResult(value: unknown): RunResult {
  const externalSources =
    typeof value === "object" &&
    value !== null &&
    (value as { externalSources?: unknown }).externalSources === true;
  return { externalSources };
}

// Server-shaped, so validated rather than cast: a params value that is not a string is dropped
// instead of reaching react-intl, where an object argument renders as "[object Object]".
function parseProgress(value: unknown): AgentProgress | null {
  if (typeof value !== "object" || value === null) return null;
  const { phase, params } = value as { phase?: unknown; params?: unknown };
  if (typeof phase !== "string" || phase === "") return null;
  const entries =
    typeof params === "object" && params !== null
      ? Object.entries(params).filter((e): e is [string, string] => typeof e[1] === "string")
      : [];
  const { depth } = value as { depth?: unknown };
  return {
    phase,
    params: Object.fromEntries(entries),
    ...(typeof depth === "number" && depth > 0 ? { depth } : {}),
    ...parseFilter(value),
  };
}

/** The flattened `where`, when it is there and well-formed. Anything odd is simply dropped. */
function parseFilter(value: object): { filter?: FilterDescription } {
  const { filter } = value as { filter?: unknown };
  if (typeof filter !== "object" || filter === null) return {};
  const { logic, conditions } = filter as { logic?: unknown; conditions?: unknown };
  if (typeof logic !== "string" || !Array.isArray(conditions)) return {};
  const parsed = conditions.flatMap((c): FilterCondition[] => {
    if (typeof c !== "object" || c === null) return [];
    const { field, op, value: v, values } = c as Record<string, unknown>;
    if (typeof field !== "string" || typeof op !== "string") return [];
    return [{
      field,
      op,
      ...(typeof v === "string" ? { value: v } : {}),
      values: Array.isArray(values) ? values.filter((x): x is string => typeof x === "string") : [],
    }];
  });
  return parsed.length > 0 ? { filter: { logic, conditions: parsed } } : {};
}

export interface AgentBindingContext {
  listingId?: string;
  matchId?: string;
  companyId?: string;
}

// A typed turn INTENT: "this turn was started by that button, not typed". It rides as its own
// `forwardedProps.action` field, deliberately NOT as a `context` key — `context` is the entity-id
// allowlist (see the comment on `bindingContext` below), not a verb channel
// (smycles/development/services/agent-service#95). It must be listed on the BFF's body schemas as
// well, or it never arrives: see `AGENT_ACTIONS` in apps/bff/src/routes/agent-proxy.ts.
export type AgentAction = "GENERATE_MATCH_REQUIREMENTS" | "GENERATE_GROWTH_NOTE";

// agent-service streams the MCP CallToolResult outcome as the TOOL_CALL_RESULT `content`: a JSON
// string `{"type":"tool_result","source":…,"size":…,"isError":bool,"resultId":…}`. A *completed*
// tool call can still be a failure (e.g. genesis rejected a write) — that surfaces as
// `isError:true`, not a run error. Absent/malformed ⇒ success with no result id, matching results
// that carry no flag.
function parseToolResult(content: unknown): ToolResultMeta {
  if (typeof content !== "string") return { isError: false, resultId: null };
  try {
    const parsed = JSON.parse(content) as { isError?: unknown; resultId?: unknown };
    return {
      isError: parsed.isError === true,
      resultId: typeof parsed.resultId === "string" && parsed.resultId ? parsed.resultId : null,
    };
  } catch {
    return { isError: false, resultId: null };
  }
}

// Run one chat turn. Resolves when the run finishes (RUN_FINISHED / RUN_ERROR / abort); rejects on
// transport failure before/while streaming. `threadId` is the agent-service session id (null →
// let the client mint one; the server creates the session under it and echoes it in RUN_STARTED).
// Which half of the company search answers a question: the companies' own TEXTS, their expertise
// LABELS, or both merged. The AGENT-SERVICE'S vocabulary verbatim, so nothing translates between the
// composer's words and the door's (@external-adr AGENT-SERVICE-0043-the-reader-can-overrule-the-company-door). Auto is the absence of a value.
export type CompanyRoute = "description_and_label" | "description" | "label";

// EVERY route the door can REPORT, which is not the same set. `named` is derived — the door picks
// it when the question names its companies — and the reader cannot ask for it, so it must stay out
// of `CompanyRoute`, whose exhaustive `Record` in `CompanySearchMode` would then demand a menu
// entry for a mode nobody may select. It was simply missing when the fourth route landed, so the
// progress text's `named` arm was hand-maintained with nothing checking it.
export type ReportedCompanyRoute = CompanyRoute | "named";

export async function runAguiTurn(
  agent: HttpAgent,
  opts: {
    message: string;
    section: string;
    threadId: string | null;
    uiContext: Context[];
    // Tool-binding/gating context (agent-service reads it from `forwardedProps.context`, NOT the
    // top-level AG-UI `context` which only feeds the system prompt). Carries the focused entity id
    // (e.g. `{matchId}`) so agent-service offers that match's write tools; omit when nothing focused.
    bindingContext?: AgentBindingContext;
    // Typed intent for a button-started turn (see AgentAction); omitted for anything the user typed.
    action?: AgentAction;
    // What this question may search. Sent as `searchSources`, and for an agent-service from before that field
    // also as the old `webSearch` flag; the agent-service reads the flag only when `searchSources` is absent.
    searchSources?: readonly SearchSource[];
    // The route the reader demanded for a company question, overriding the agent-service's door.
    // Omitted on Auto — an absent field is what "the door decides" means on the wire, so a turn from
    // a client that never heard of this keeps its shape exactly.
    companyRoute?: CompanyRoute | null;
    // How close a label must be to the asked-for word. Omitted on Automatic, exactly as the route is:
    // an absent field means "use the service's own default", so an older client's turn is unchanged.
    labelThreshold?: number | null;
    // The open Studio notebook; sent only on a save/studio turn, where the agent renders it as data.
    studioNotebook?: StudioNotebookContext | null;
    // A Grow note request's shape: how many examples it holds and the person's own wishes for it. Only with
    // the GENERATE_GROWTH_NOTE action; absent means three and none (spark#1218, agent-service#133).
    growNote?: { items: number; wishes?: string };
    // A Growth turn on which a SaveGrowNote call would save rather than draw the neutral notice. The agent-service
    // offers the tool only when this is true; sent only then, so every other turn keeps its shape exactly.
    growSaveAvailable?: boolean;
    abortController: AbortController;
    // Idle window before a stalled turn is aborted. Defaults to DEFAULT_TURN_IDLE_MS.
    idleMs?: number;
  },
  handlers: AguiTurnHandlers,
): Promise<void> {
  // Always assign: HttpAgent retains its threadId across runs, so on a reused agent instance a
  // null (new chat) must mint a FRESH id — the server load-or-creates the session under whatever
  // threadId rides up, so a stale one would silently continue the previous conversation.
  agent.threadId = opts.threadId ?? crypto.randomUUID();
  // Server-authoritative history: only the latest user message rides up (see module comment).
  agent.setMessages([{ id: crypto.randomUUID(), role: "user", content: opts.message }]);

  const subscriber: AgentSubscriber = {
    onRunStartedEvent: ({ event }) => {
      handlers.onThreadId(event.threadId);
      handlers.onRunId(event.runId);
    },
    onRunFinishedEvent: ({ event }) => handlers.onRunResult(parseRunResult(event.result)),
    onTextMessageContentEvent: ({ event }) => handlers.onDelta(event.delta),
    onToolCallEndEvent: ({ event, toolCallName, toolCallArgs }) =>
      handlers.onToolCall(toolCallName, toolCallArgs, event.toolCallId),
    onToolCallResultEvent: ({ event }) =>
      // AG-UI 1.0 widened content to string | ContentPart[]; agent-service sends the JSON as text.
      handlers.onToolResult(event.toolCallId, parseToolResult(contentToText(event.content))),
    onCustomEvent: ({ event }) => {
      if (event.name === "suggestions" && Array.isArray(event.value)) {
        handlers.onSuggestions(event.value.filter((v): v is string => typeof v === "string"));
        return;
      }
      if (event.name === "progress") {
        const progress = parseProgress(event.value);
        if (progress) handlers.onProgress(progress);
        return;
      }
      if (event.name === "notice") {
        const code = parseAgentNoticeCode(
          typeof event.value === "object" && event.value !== null ? event.value.code : undefined,
        );
        if (code) handlers.onNotice(code);
        return;
      }
      // Silence here is what made an unknown CUSTOM name impossible to debug from the browser.
      console.debug("[agui] unhandled custom event", event.name, event.value);
    },
    // A genuine RUN_ERROR from the stream. One that follows a stall never arrives here: the
    // stall already failed the turn through onStall.
    onRunErrorEvent: () => handlers.onError(),
  };

  await runWatchedTurn(
    agent,
    {
      context: opts.uiContext,
      forwardedProps: {
        section: opts.section,
        webSearch: opts.searchSources?.includes("web") === true,
        searchSources: [...(opts.searchSources ?? [])],
        ...(opts.action ? { action: opts.action } : {}),
        ...(opts.companyRoute ? { companyRoute: opts.companyRoute } : {}),
        // `!= null`, not truthiness: 0 is a real threshold the reader can pick, and `0 ? …` drops it.
        ...(opts.labelThreshold != null ? { labelThreshold: opts.labelThreshold } : {}),
        ...(opts.bindingContext ? { context: opts.bindingContext } : {}),
        ...(opts.studioNotebook ? { studioNotebook: opts.studioNotebook } : {}),
        ...(opts.growNote ? { growNote: opts.growNote } : {}),
        ...(opts.growSaveAvailable ? { growSaveAvailable: true } : {}),
      },
      abortController: opts.abortController,
      idleMs: opts.idleMs,
      onStall: () => handlers.onError(),
    },
    subscriber,
  );
}
